import { withRequestDeadline } from '@/lib/readRequest';
import { useCallback, useEffect, useRef, useState } from 'react';

// Retain only the current identity's last successful read. Late responses never cross identities.
export function useReadResource<T>(identity: string, read: () => Promise<T>, enabled = true) {
  const generation = useRef(0);
  const [state, setState] = useState<{ identity: string; data?: T; error?: unknown; loading: boolean }>({ identity, loading: enabled });
  const refresh = useCallback(async () => {
    const request = ++generation.current;
    if (!enabled) { setState({ identity, loading: false }); return false; }
    setState(previous => ({ identity, data: previous.identity === identity ? previous.data : undefined, loading: true }));
    try {
      const data = await withRequestDeadline(read());
      if (request !== generation.current) return false;
      setState({ identity, data, loading: false });
      return true;
    } catch (error) {
      if (request !== generation.current) return false;
      setState(previous => ({ identity, data: previous.identity === identity ? previous.data : undefined, error, loading: false }));
      return false;
    }
  }, [identity, read, enabled]);
  useEffect(() => { void refresh(); return () => { generation.current += 1; }; }, [refresh]);
  const current = state.identity === identity ? state : { identity, loading: enabled };
  return { ...current, refresh, stale: current.data !== undefined && !!current.error };
}
